import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import DateInput from '../components/DateInput';
import './Dashboard.css';

const Dashboard = () => {
    const { user } = useAuth();
    const [employeeData, setEmployeeData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const isAdmin = user?.role_obj?.name?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'admin';

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                if (isAdmin) {
                    const params = {};
                    if (startDate) params.start_date = startDate;
                    if (endDate) params.end_date = endDate;
                    
                    const response = await api.get('/tasks/dashboard/employee-summary', { params });
                    setEmployeeData(response.data?.data || []);
                }
            } catch (err) {
                console.error('Dashboard error:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        setLoading(true);
        fetchDashboardData();
    }, [isAdmin, startDate, endDate]);

    if (loading) {
        return (
            <div className="dashboard-loading">
                <div className="loading-spinner">Loading...</div>
            </div>
        );
    }

    if (!isAdmin) {
        return (
            <div className="dashboard-container">
                <h1>Dashboard</h1>
                <div className="no-access">
                    <p>You do not have permission to access this dashboard. Admin access required.</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-error">
                <h2>Error Loading Dashboard</h2>
                <p>{error}</p>
                <button onClick={() => window.location.reload()}>Retry</button>
            </div>
        );
    }

    // Get all unique statuses across all employees
    const allStatuses = new Set();
    employeeData.forEach(emp => {
        Object.keys(emp.byStatus || {}).forEach(status => allStatuses.add(status));
    });
    const statusList = Array.from(allStatuses).sort();
    // reorder statusList to have the order: backlog, to_do, inprogress, done, l1_-_approval_pending, l2_-_approval_pending, minutes_to_task_-_conversion_pending, l1_-_review_pending, l2_-_review_pending
    const statusOrder = ['follow_up', 'to_do', 'inprogress', 'done', 'l1_-_approval_pending', 'l2_-_approval_pending', 'minutes_to_task_-_conversion_pending', 'l1_-_review_pending', 'l2_-_review_pending'];
    statusList.sort((a, b) => {
        const indexA = statusOrder.indexOf(a);
        const indexB = statusOrder.indexOf(b);
        if (indexA === -1 && indexB === -1) return a.localeCompare(b); // both not found, sort alphabetically
        if (indexA === -1) return 1; // a not found, b comes first
        if (indexB === -1) return -1; // b not found, a comes first
        return indexA - indexB; // both found, sort by order
    });



    // Calculate status-wise totals
    const statusTotals = {};
    let grandTotalTasks = 0;
    let grandTotalAmount = 0;
    
    statusList.forEach(status => {
        statusTotals[status] = { count: 0, amount: 0 };
    });

    employeeData.forEach(emp => {
        statusList.forEach(status => {
            const statusData = emp.byStatus[status] || { count: 0, amount: 0 };
            statusTotals[status].count += statusData.count;
            statusTotals[status].amount += statusData.amount;
        });
        grandTotalTasks += emp.totalTasks;
        grandTotalAmount += emp.totalAmount;
    });

    return (
        <div className="dashboard-container">
            <h1>Employee Task Dashboard</h1>
            
            {/* Date Filters */}
            <div className="dashboard-filters">
                <div className="filter-date-wrapper">
                    <DateInput
                        label="From Date"
                        value={startDate}
                        onChange={(val) => setStartDate(val)}
                    />
                </div>
                <div className="filter-date-wrapper">
                    <DateInput
                        label="To Date"
                        value={endDate}
                        onChange={(val) => setEndDate(val)}
                    />
                </div>
                {/* {(startDate || endDate) && (
                    <button 
                        className="clear-filters-btn"
                        onClick={() => {
                            setStartDate('');
                            setEndDate('');
                        }}
                    >
                        Clear Filters
                    </button>
                )} */}
            </div>

            {employeeData.length === 0 ? (
                <div className="no-data">
                    <p>No employee data available.</p>
                </div>
            ) : (
                <div className="dashboard-table-wrapper">
                    <table className="dashboard-table">
                        <thead>
                            <tr>
                                <th>Employee</th>
                                {statusList.map(status => (
                                    <th key={status} className="status-column">
                                        <span className="status-header">{status.replace(/_/g, ' ').toUpperCase()}</span>
                                    </th>
                                ))}
                                <th className="total-column">TOTAL</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employeeData.map(employee => (
                                <tr key={employee.id}>
                                    <td className="employee-name">{employee.name}</td>
                                    {statusList.map(status => {
                                        // columns should be status order should be backlog,todo,in_progress,review,completed,cancelled

                                        const statusData = employee.byStatus[status] || { count: 0, amount: 0 };
                                        return (
                                            <td key={status} className="status-data">
                                                <div className="status-cell">
                                                    <div className="task-count">
                                                        {statusData.count} task{statusData.count !== 1 ? 's' : ''}
                                                    </div>
                                                    <div className="task-amount">
                                                        (₹ {statusData.amount?.toLocaleString('en-IN') || '0'})
                                                    </div>
                                                </div>
                                            </td>
                                        );
                                    })}
                                    <td className="total-data">
                                        <div className="total-cell">
                                            <div className="task-count">{employee.totalTasks} task{employee.totalTasks !== 1 ? 's' : ''}</div>
                                            <div className="task-amount">
                                                (₹ {employee.totalAmount?.toLocaleString('en-IN') || '0'})
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {/* Summary row showing status-wise totals */}
                            <tr className="summary-row">
                                <td className="summary-label">Total</td>
                                {statusList.map(status => {
                                    const total = statusTotals[status] || { count: 0, amount: 0 };
                                    return (
                                        <td key={status} className="summary-data">
                                            <div className="status-cell">
                                                <div className="task-count">
                                                    {total.count} task{total.count !== 1 ? 's' : ''}
                                                </div>
                                                <div className="task-amount">
                                                    (₹ {total.amount?.toLocaleString('en-IN') || '0'})
                                                </div>
                                            </div>
                                        </td>
                                    );
                                })}
                                <td className="summary-total-data">
                                    <div className="total-cell">
                                        <div className="task-count">{grandTotalTasks} task{grandTotalTasks !== 1 ? 's' : ''}</div>
                                        <div className="task-amount">
                                            (₹ {grandTotalAmount?.toLocaleString('en-IN') || '0'})
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default Dashboard;